import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  normalizeSlotMix,
  type FxNode,
  type InstrumentSlot,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import {
  registerMixerMasterMeter,
  registerMixerTrackMeter,
  setMixerMeterTelemetry,
} from "../lib/mixerMeters";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";
import { ParamKnob } from "./ParamKnob";
import { Checkbox } from "./ui/Checkbox";
import { VerticalFxStrip } from "./VerticalFxStrip";

const STORAGE_KEY = "audio-lab:mixer-modal";
const DEFAULT_WIDTH = 920;
/** ~2/3 of the previous 640px default height. */
const DEFAULT_HEIGHT = Math.round(640 * (2 / 3));
const MIN_WIDTH = 480;
const MIN_HEIGHT = 280;
const CHANNEL_WIDTH = 148;

type PanelLayout = { left: number; top: number; width: number; height: number };

function clampSize(width: number, height: number): { width: number; height: number } {
  const maxW = Math.max(MIN_WIDTH, window.innerWidth - 16);
  const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 16);
  return {
    width: Math.min(maxW, Math.max(MIN_WIDTH, Math.round(width))),
    height: Math.min(maxH, Math.max(MIN_HEIGHT, Math.round(height))),
  };
}

function clampPos(pos: { left: number; top: number }, width: number, height: number): {
  left: number;
  top: number;
} {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - height - 8);
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

function defaultLayout(): PanelLayout {
  const size = clampSize(DEFAULT_WIDTH, DEFAULT_HEIGHT);
  const left = Math.max(12, (window.innerWidth - size.width) / 2);
  const top = Math.max(12, Math.min(72, window.innerHeight * 0.08));
  const pos = clampPos({ left, top }, size.width, size.height);
  return { ...pos, ...size };
}

function loadLayout(): PanelLayout | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelLayout>;
    if (
      typeof parsed.left !== "number"
      || typeof parsed.top !== "number"
    ) {
      return undefined;
    }
    const size = clampSize(
      typeof parsed.width === "number" ? parsed.width : DEFAULT_WIDTH,
      typeof parsed.height === "number" ? parsed.height : DEFAULT_HEIGHT,
    );
    const pos = clampPos({ left: parsed.left, top: parsed.top }, size.width, size.height);
    return { ...pos, ...size };
  } catch {
    return undefined;
  }
}

function saveLayout(layout: PanelLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

function clampLayout(layout: PanelLayout): PanelLayout {
  const size = clampSize(layout.width, layout.height);
  const pos = clampPos(layout, size.width, size.height);
  return { ...pos, ...size };
}

/**
 * Floating Mixer — track channels + sticky master on the right.
 * Per-channel FX use vertical accordions (not the horizontal FxDock strip).
 */
export function MixerModal() {
  const open = useStoreSelector(uiStore, s => s.mixerOpen);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const slotMix = useStoreSelector(projectStore, s => s.slotMix);
  const slotFx = useStoreSelector(projectStore, s => s.slotFx);
  const masterFx = useStoreSelector(projectStore, s => s.fx);
  const clip = useStoreSelector(projectStore, s => s.clip);
  const activePatternId = useStoreSelector(uiStore, s => s.activePatternId);
  const trackMuted = useStoreSelector(uiStore, s => s.trackMuted);
  const trackSolo = useStoreSelector(uiStore, s => s.trackSolo);
  const fxBypass = useStoreSelector(uiStore, s => s.fxBypass);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [layout, setLayout] = useState<PanelLayout>(() => loadLayout() ?? defaultLayout());
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [trackDragFrom, setTrackDragFrom] = useState<number | null>(null);
  const [trackDropIndex, setTrackDropIndex] = useState<number | null>(null);

  const trackCount = instruments.length;

  const trackNames = useMemo(() => {
    const pattern =
      clip.patterns.find(p => p.id === activePatternId)
      ?? clip.patterns[0];
    return Array.from({ length: trackCount }, (_, i) => {
      const name = pattern?.tracks[i]?.name?.trim();
      return name || `I${i + 1}`;
    });
  }, [clip, activePatternId, trackCount]);

  useEffect(() => {
    if (!open) {
      setMixerMeterTelemetry(null);
      return;
    }
    setMixerMeterTelemetry(() => getLabActions()?.getTelemetry?.() ?? {
      tracks: [],
      master: { rmsL: 0, rmsR: 0, peakL: 0, peakR: 0 },
    });
    setLayout(prev => {
      const next = clampLayout(loadLayout() ?? prev);
      saveLayout(next);
      return next.left === prev.left
        && next.top === prev.top
        && next.width === prev.width
        && next.height === prev.height
        ? prev
        : next;
    });
    return () => {
      setMixerMeterTelemetry(null);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onResize(): void {
      setLayout(prev => {
        const next = clampLayout(prev);
        saveLayout(next);
        return next;
      });
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey, true);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    setDragging(false);
    setResizing(false);
    patchUi({ mixerOpen: false });
    getLabActions()?.setStatus?.("Mixer closed.");
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const targetEl = event.target as HTMLElement | null;
    if (targetEl?.closest("button, input, select, textarea, label")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      setLayout(prev => {
        const pos = clampPos(
          {
            left: moveEvent.clientX - offsetX,
            top: moveEvent.clientY - offsetY,
          },
          prev.width,
          prev.height,
        );
        const next = { ...prev, ...pos };
        saveLayout(next);
        return next;
      });
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function beginResize(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = layout.width;
    const startH = layout.height;
    const startLeft = layout.left;
    const startTop = layout.top;
    setResizing(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const size = clampSize(
        startW + (moveEvent.clientX - startX),
        startH + (moveEvent.clientY - startY),
      );
      const pos = clampPos({ left: startLeft, top: startTop }, size.width, size.height);
      const next = { ...pos, ...size };
      saveLayout(next);
      setLayout(next);
    };

    const onUp = (): void => {
      setResizing(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal mixer-modal fixed z-50 flex flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)]${
        dragging || resizing ? " select-none" : ""
      }`}
      role="dialog"
      aria-modal="false"
      aria-label="Mixer"
      style={{
        left: layout.left,
        top: layout.top,
        width: layout.width,
        height: layout.height,
      }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title text-[12px] font-semibold text-lab-text">
          Mixer
        </span>
        <button
          type="button"
          className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
          title="Close"
          aria-label="Close"
          onPointerDown={event => event.stopPropagation()}
          onClick={close}
        >
          Close
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto overflow-y-hidden px-2 py-2">
          {Array.from({ length: trackCount }, (_, i) => {
            const slot = i as InstrumentSlot;
            const mix = normalizeSlotMix(slotMix[slot]);
            const muted = trackMuted[slot] === true;
            const solo = trackSolo[slot] === true;
            const fx = slotFx[slot] ?? [];
            return (
              <MixerChannel
                key={slot}
                label={trackNames[slot]!}
                slotLabel={`I${slot + 1}`}
                kind="track"
                trackIndex={slot}
                muted={muted}
                solo={solo}
                volume={mix.volume}
                pan={mix.pan}
                effects={fx}
                dragActive={trackDragFrom === slot}
                dropTarget={
                  trackDropIndex === slot
                  && trackDragFrom !== null
                  && trackDragFrom !== slot
                }
                onTrackDragStart={event => {
                  setTrackDragFrom(slot);
                  event.dataTransfer.effectAllowed = "move";
                  event.dataTransfer.setData("application/x-adam-track", String(slot));
                  event.dataTransfer.setData("text/plain", `track:${slot}`);
                }}
                onTrackDragOver={event => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setTrackDropIndex(slot);
                }}
                onTrackDrop={event => {
                  event.preventDefault();
                  const raw =
                    event.dataTransfer.getData("application/x-adam-track")
                    || event.dataTransfer.getData("text/plain").replace(/^track:/, "");
                  const from = trackDragFrom ?? Number(raw);
                  setTrackDragFrom(null);
                  setTrackDropIndex(null);
                  if (!Number.isFinite(from) || from === slot) {
                    return;
                  }
                  getLabActions()?.reorderTracks(from, slot);
                }}
                onTrackDragEnd={() => {
                  setTrackDragFrom(null);
                  setTrackDropIndex(null);
                }}
                onMute={() => getLabActions()?.toggleMute?.(slot)}
                onSolo={() => getLabActions()?.toggleSolo?.(slot)}
                onVolume={volume => getLabActions()?.setSlotMix?.(slot, { volume })}
                onPan={pan => getLabActions()?.setSlotMix?.(slot, { pan })}
                onCommitFx={(next, status) => {
                  getLabActions()?.applySlotFxStack?.(slot, next);
                  if (status) {
                    getLabActions()?.setStatus?.(status);
                  }
                }}
                learnPrefix={`/track/${slot}`}
                onSelect={() => getLabActions()?.openInstrument?.(slot)}
              />
            );
          })}
        </div>

        <div className="flex shrink-0 border-l border-lab-line bg-lab-panel-bar/40 px-2 py-2">
          <MixerChannel
            label="Master"
            slotLabel="M"
            kind="master"
            trackIndex={null}
            muted={false}
            solo={false}
            volume={1}
            pan={0}
            effects={masterFx}
            bypass={fxBypass}
            onBypassChange={on => getLabActions()?.setFxBypass?.(on)}
            learnPrefix="/master"
            onCommitFx={(next, status) => {
              getLabActions()?.applyFxStack?.(next);
              if (status) {
                getLabActions()?.setStatus?.(status);
              }
            }}
          />
        </div>
      </div>

      <div
        role="separator"
        aria-label="Resize mixer"
        title="Resize"
        className="absolute right-0 bottom-0 z-10 h-4 w-4 cursor-nwse-resize touch-none"
        onPointerDown={beginResize}
      >
        <svg
          viewBox="0 0 16 16"
          width={14}
          height={14}
          className="absolute right-0.5 bottom-0.5 text-lab-muted opacity-70"
          aria-hidden
        >
          <path
            d="M5 15h2M9 15h2M13 11v2M13 7v2M13 15V13M11 15h2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>,
    document.body,
  );
}

function MixerChannel({
  label,
  slotLabel,
  kind,
  trackIndex,
  muted,
  solo,
  volume,
  pan,
  effects,
  onMute,
  onSolo,
  onVolume,
  onPan,
  onCommitFx,
  onSelect,
  bypass,
  onBypassChange,
  learnPrefix,
  dragActive,
  dropTarget,
  onTrackDragStart,
  onTrackDragOver,
  onTrackDrop,
  onTrackDragEnd,
}: {
  label: string;
  slotLabel: string;
  kind: "track" | "master";
  trackIndex: number | null;
  muted: boolean;
  solo: boolean;
  volume: number;
  pan: number;
  effects: FxNode[];
  onMute?: () => void;
  onSolo?: () => void;
  onVolume?: (v: number) => void;
  onPan?: (v: number) => void;
  onCommitFx: (next: FxNode[], status?: string) => void;
  onSelect?: () => void;
  bypass?: boolean;
  onBypassChange?: (on: boolean) => void;
  /** `/track/{n}` or `/master` — used for Learn address paths. */
  learnPrefix?: string;
  dragActive?: boolean;
  dropTarget?: boolean;
  onTrackDragStart?: (event: ReactDragEvent) => void;
  onTrackDragOver?: (event: ReactDragEvent) => void;
  onTrackDrop?: (event: ReactDragEvent) => void;
  onTrackDragEnd?: () => void;
}) {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) {
      return;
    }
    if (kind === "master") {
      registerMixerMasterMeter({ left, right });
      return () => registerMixerMasterMeter(null);
    }
    if (trackIndex != null) {
      registerMixerTrackMeter(trackIndex, { left, right });
      return () => registerMixerTrackMeter(trackIndex, null);
    }
  }, [kind, trackIndex]);

  return (
    <div
      className={`flex h-full shrink-0 flex-col gap-1.5 overflow-hidden rounded-md border border-lab-line bg-lab-panel-deep px-1.5 pt-1.5 pb-2 ${
        muted ? "opacity-60" : ""
      }${dropTarget ? " outline outline-1 outline-lab-accent -outline-offset-1" : ""}${
        dragActive ? " opacity-50" : ""
      }`}
      style={{ width: CHANNEL_WIDTH }}
      onDragOver={kind === "track" ? onTrackDragOver : undefined}
      onDrop={kind === "track" ? onTrackDrop : undefined}
    >
      <div className="flex min-w-0 items-center gap-1">
        {kind === "track" ? (
          <Checkbox
            variant="power"
            checked={!muted}
            title={muted ? "Unmute" : "Mute"}
            aria-label={muted ? `Unmute ${label}` : `Mute ${label}`}
            onCheckedChange={() => onMute?.()}
          />
        ) : null}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1 border-0 bg-transparent p-0 text-left"
          title={
            kind === "track"
              ? "Drag to reorder · click to open instrument"
              : undefined
          }
          disabled={!onSelect && kind !== "track"}
          draggable={kind === "track"}
          onDragStart={event => {
            if (kind !== "track") {
              return;
            }
            onTrackDragStart?.(event);
          }}
          onDragEnd={onTrackDragEnd}
          onClick={() => onSelect?.()}
        >
          <span className="shrink-0 rounded border border-lab-line px-1 py-px text-[9px] font-semibold text-lab-muted">
            {slotLabel}
          </span>
          <span className="min-w-0 truncate text-[11px] font-semibold text-lab-text">
            {label}
          </span>
        </button>
      </div>

      <div
        className="relative mx-auto h-[88px] w-9 overflow-hidden rounded border border-lab-line bg-[#0a0c10]"
        aria-hidden
      >
        <div className="absolute inset-x-0 bottom-0 top-0 flex justify-center gap-0.5 px-1 py-1">
          <div className="relative h-full w-[7px] overflow-hidden rounded-[1px] bg-black/40">
            <div
              ref={leftRef}
              className="absolute inset-x-0 bottom-0 h-full origin-bottom rounded-[1px] bg-[linear-gradient(to_top,#3d8bfd_0%,#5eead4_70%,#fbbf24_92%,#f87171_100%)]"
              style={{ transform: "scaleY(0)" }}
            />
          </div>
          <div className="relative h-full w-[7px] overflow-hidden rounded-[1px] bg-black/40">
            <div
              ref={rightRef}
              className="absolute inset-x-0 bottom-0 h-full origin-bottom rounded-[1px] bg-[linear-gradient(to_top,#3d8bfd_0%,#5eead4_70%,#fbbf24_92%,#f87171_100%)]"
              style={{ transform: "scaleY(0)" }}
            />
          </div>
        </div>
      </div>

      {kind === "track" ? (
        <>
          <div className="flex justify-center gap-1">
            <button
              type="button"
              title="Solo"
              aria-label={`Solo ${label}`}
              aria-pressed={solo}
              className={`inline-grid h-6 w-6 place-items-center rounded border p-0 text-[10px] leading-none font-bold ${
                solo
                  ? "border-[#e6b35a] bg-[#e6b35a] text-slate-900"
                  : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
              }`}
              onClick={onSolo}
            >
              S
            </button>
            <button
              type="button"
              title="Mute"
              aria-label={`Mute ${label}`}
              aria-pressed={muted}
              className={`inline-grid h-6 w-6 place-items-center rounded border p-0 text-[10px] leading-none font-bold ${
                muted
                  ? "border-slate-400 bg-slate-400 text-slate-900"
                  : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
              }`}
              onClick={onMute}
            >
              M
            </button>
          </div>
          <div className="flex items-center justify-center gap-2">
            <ParamKnob
              label="Vol"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              defaultValue={1}
              density="compact"
              learnPath={
                kind === "track" && learnPrefix
                  ? `${learnPrefix}/mix/volume`
                  : undefined
              }
              onChange={v => onVolume?.(v)}
            />
            <ParamKnob
              label="Pan"
              min={-1}
              max={1}
              step={0.01}
              value={pan}
              defaultValue={0}
              density="compact"
              learnPath={
                kind === "track" && learnPrefix
                  ? `${learnPrefix}/mix/pan`
                  : undefined
              }
              format={v => {
                if (Math.abs(v) < 0.02) {
                  return "C";
                }
                return v < 0 ? `L${Math.round(Math.abs(v) * 100)}` : `R${Math.round(v * 100)}`;
              }}
              onChange={v => onPan?.(v)}
            />
          </div>
        </>
      ) : null}

      <div className="mt-auto flex min-h-0 flex-1 flex-col gap-1 overflow-hidden">
        <div className="flex items-center justify-between gap-1 px-0.5">
          <div className="text-[10px] font-semibold tracking-[0.03em] text-lab-muted uppercase">
            FX
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pr-0.5">
          <VerticalFxStrip
            effects={effects}
            onCommit={onCommitFx}
            bypass={kind === "master" ? bypass : undefined}
            onBypassChange={kind === "master" ? onBypassChange : undefined}
            learnBypassPath={
              kind === "master" && learnPrefix ? `${learnPrefix}/fxBypass` : undefined
            }
            learnFxPrefix={
              learnPrefix
                ? kind === "master"
                  ? `${learnPrefix}/fx`
                  : `${learnPrefix}/fx`
                : undefined
            }
            emptyHint={kind === "master" ? "Empty master bus." : "Empty insert."}
          />
        </div>
      </div>
    </div>
  );
}
